/* Color values here mirror the CSS custom properties in src/styles/global.css
   (light block + the `:root[data-theme="dark"]` block). When changing a
   color update both files to stay in sync. */
import { alpha, createTheme, type PaletteMode, type Theme } from '@mui/material/styles';

const palettes = {
  light: {
    // --color-primary  ≈ oklch(0.58 0.20 277) — mock's brand-500/600
    primary:    { main: '#6366f1', dark: '#4f46e5', light: '#818cf8' },
    // --color-secondary ≈ oklch(0.56 0.17 300)
    secondary:  { main: '#7c3aed', dark: '#5b21b6', light: '#a78bfa' },
    background: { default: '#f8f9fb', paper: '#ffffff' }, // --color-bg / --color-surface
    text:       { primary: '#1e2a3a', secondary: '#6b7a8d' }, // --color-ink / --color-muted
    divider:    '#e8ecf0', // --color-line
    success:    { main: '#16a34a' }, // --color-success
    warning:    { main: '#d97706' }, // --color-warning
    error:      { main: '#dc2626' }, // --color-error
  },
  dark: {
    // Lighter tints read better on a dark surface — mirrors the mock's
    // `dark:text-{color}-400` convention (light mode uses the deeper shade).
    primary:    { main: '#818cf8', dark: '#6366f1', light: '#a5b4fc' },
    secondary:  { main: '#a78bfa', dark: '#7c3aed', light: '#c7b6fc' },
    background: { default: '#090d16', paper: '#111726' }, // --color-bg / --color-surface (dark)
    text:       { primary: '#e7ebf5', secondary: '#8b96ab' }, // --color-ink / --color-muted (dark)
    divider:    '#1f293d', // --color-line (dark)
    success:    { main: '#34d399' },
    warning:    { main: '#fbbf24' },
    error:      { main: '#f87171' },
  },
} as const;

export function createAppTheme(mode: PaletteMode): Theme {
  const p = palettes[mode];
  const dark = mode === 'dark';

  return createTheme({
    palette: { mode, ...p },

    shape: { borderRadius: 9 }, // matches --radius: 9px

    typography: {
      // --font-sans — Hanken Grotesk is loaded globally in src/styles/global.css
      fontFamily: '"Hanken Grotesk", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 800, lineHeight: 1.2 },
      h2: { fontWeight: 700, lineHeight: 1.25 },
      h3: { fontWeight: 700, lineHeight: 1.3 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 600 },
      body2:    { fontSize: '0.875rem' },
      caption:  { fontSize: '0.8125rem', letterSpacing: '0.01em' },
      overline: { fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.06em' },
      button:   { fontWeight: 600, textTransform: 'none' },
    },

    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },

      MuiAppBar: {
        defaultProps: { color: 'inherit', elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: dark ? 'none' : '0 1px 2px rgba(11,18,32,0.04)',
          }),
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({ borderRight: `1px solid ${theme.palette.divider}` }),
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root:      { borderRadius: 7, paddingLeft: 16, paddingRight: 16 },
          sizeSmall: { borderRadius: 6 },
        },
      },

      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 7 } },
      },

      MuiListItemButton: {
        styleOverrides: {
          // Callback form (not a plain object) so the selected accent bar can
          // reuse the theme's own brand colors instead of re-hardcoding them.
          root: ({ theme }) => ({
            borderRadius: 7,
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, dark ? 0.18 : 0.08),
              color: dark ? theme.palette.primary.light : theme.palette.primary.dark,
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, dark ? 0.24 : 0.12) },
              // Left accent bar, echoing the brand gradient used on the
              // marketing site and sign-in screen. Sits in the item's own
              // horizontal margin (mx: 1 at every call site), so it reads as
              // flush with the sidebar edge rather than overlapping content.
              '&::before': {
                content: '""',
                position: 'absolute',
                left: -8,
                top: '20%',
                bottom: '20%',
                width: 3,
                borderRadius: 3,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
            },
          }),
        },
      },

      MuiChip: {
        styleOverrides: { root: { fontWeight: 500, borderRadius: 6 } },
      },

      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({ fontWeight: 600, backgroundColor: theme.palette.background.default }),
        },
      },

      MuiTooltip: {
        defaultProps: { arrow: true, placement: 'top' },
      },

      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
      },

      MuiSelect: {
        defaultProps: { size: 'small' },
      },

      MuiAlert: {
        styleOverrides: { root: { borderRadius: 9 } },
      },

      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 13 } },
      },
    },
  });
}
